import type { KnowledgeViewKey } from "../knowledge/types";

/**
 * Feature capabilities the UI can gate on. These intentionally mirror the
 * backend feature registry (`docs/10-feature-registry.md`) and the reporting
 * entitlement matrix (`cloudox/entitlements/`) WITHOUT encoding any tier names
 * or `if tier === "enterprise"` checks in components. Availability is resolved
 * once (server-side in the future, all-enabled today) and read through the
 * {@link Entitlements} contract.
 */
export type FeatureKey =
  | "knowledge_views"
  | "diagrams"
  | "cost_intelligence"
  | "environment_evolution"
  | "quality_intelligence"
  | "ai_narration"
  | "assistant"
  | "exports"
  | "search"
  | "admin_console";

export const ALL_FEATURES: readonly FeatureKey[] = [
  "knowledge_views",
  "diagrams",
  "cost_intelligence",
  "environment_evolution",
  "quality_intelligence",
  "ai_narration",
  "assistant",
  "exports",
  "search",
  "admin_console",
];

/** Declarative source for {@link createEntitlements}. */
export interface EntitlementsConfig {
  /** Optional human label for the active plan (display only, never branched on). */
  readonly tier?: string;
  /** Per-feature overrides. Anything omitted defaults to enabled. */
  readonly features?: Partial<Record<FeatureKey, boolean>>;
  /** Allowed views, or `"all"`. Defaults to `"all"`. */
  readonly views?: readonly KnowledgeViewKey[] | "all";
}

/** The capability contract the UI reads. Pluggable for a future API source. */
export interface Entitlements {
  readonly tier?: string;
  isFeatureEnabled(feature: FeatureKey): boolean;
  isViewEnabled(view: KnowledgeViewKey): boolean;
}
